import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { FaltaMatriculadoConsolidado, FrequenciaFaltasConsolidado } from '../../../core/api/frequencia-faltas-consolidado';
import { FrequenciaService } from '../../../core/api/frequencia.service';
import { PessoaService } from '../../../core/api/pessoa.service';
import {
  ContextoMensagemFaltas,
  MENSAGEM_FALTAS_PADRAO,
  TOKENS_MENSAGEM,
  carregarMensagemFaltas,
  limparMensagemFaltas,
  linkWhatsapp,
  renderizarMensagemFaltas,
  salvarMensagemFaltas,
} from '../../../utils/mensagem-whatsapp';
import { FaltasDatasDialogComponent } from './faltas-datas-dialog.component';

import { mensagemDeErro } from '../../../utils/mensagem-erro';
@Component({
  selector: 'app-frequencia-faltas-consolidado',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
  ],
  templateUrl: './frequencia-faltas-consolidado.component.html',
  styleUrls: ['./frequencia-faltas-consolidado.component.scss'],
})
export class FrequenciaFaltasConsolidadoComponent implements OnInit {
  @ViewChild('editorMensagem') editorMensagem?: ElementRef<HTMLTextAreaElement>;

  turmaId = '';
  dados: FrequenciaFaltasConsolidado | null = null;
  carregando = false;
  erro: string | null = null;

  /** Template do WhatsApp — um só para todas as turmas, guardado no localStorage. */
  mensagem = carregarMensagemFaltas();
  editorAberto = false;
  readonly tokens = TOKENS_MENSAGEM;

  /** pessoaId → sexo ('M'/'F'), para a flexão de gênero da mensagem. */
  private sexoPorPessoa = new Map<string, string>();

  constructor(
    private frequenciaService: FrequenciaService,
    private pessoaService: PessoaService,
    private route: ActivatedRoute,
    private router: Router,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.turmaId = this.route.snapshot.paramMap.get('turmaId') ?? '';
    this.carregar();
  }

  carregar(): void {
    if (!this.turmaId) {
      this.erro = 'Turma não informada na URL.';
      return;
    }
    this.erro = null;
    this.carregando = true;
    this.frequenciaService.carregarConsolidadoFaltas(this.turmaId).subscribe({
      next: (d) => {
        this.dados = d;
        this.carregando = false;
        this.carregarSexos();
      },
      error: (err) => {
        this.carregando = false;
        this.dados = null;
        this.erro =
          mensagemDeErro(err, 'Não foi possível carregar o consolidado de faltas.');
      },
    });
  }

  /**
   * O consolidado não traz o sexo; busca em lote (`POST /pessoas/by-ids`) só para flexionar a
   * mensagem. Falha aqui é silenciosa: sem sexo, o texto cai na forma com parênteses.
   */
  private carregarSexos(): void {
    const ids = (this.dados?.matriculados ?? [])
      .map((m) => m.pessoaId)
      .filter((id): id is string => !!id && id.trim() !== '');
    if (!ids.length) return;

    this.pessoaService.buscarPorIds(ids).subscribe({
      next: (pessoas) => {
        this.sexoPorPessoa = new Map(
          (pessoas ?? [])
            .filter((p) => p?.id && p?.sexo)
            .map((p) => [String(p.id), String(p.sexo)] as const),
        );
      },
      error: () => {
        this.sexoPorPessoa = new Map();
      },
    });
  }

  /** Clique no total de faltas: popup com os dias em que o catequizando faltou. */
  abrirDatasFaltas(m: FaltaMatriculadoConsolidado): void {
    if (!m.pessoaId || !m.totalFaltas) return;
    this.dialogService.open(FaltasDatasDialogComponent, {
      context: {
        turmaId: this.turmaId,
        pessoaId: m.pessoaId,
        pessoaNome: m.pessoaNome ?? '',
        turmaNome: this.dados?.turmaNome ?? '',
      },
    });
  }

  // ------------------------------------------------------------------ mensagem

  alternarEditor(): void {
    this.editorAberto = !this.editorAberto;
  }

  salvarMensagem(): void {
    if (salvarMensagemFaltas(this.mensagem)) {
      this.toastr.success('Mensagem salva neste navegador.', 'OK');
    } else {
      this.toastr.danger('Não foi possível salvar a mensagem neste navegador.', 'Erro');
    }
  }

  restaurarMensagemPadrao(): void {
    this.mensagem = MENSAGEM_FALTAS_PADRAO;
    limparMensagemFaltas();
    this.toastr.info('Mensagem restaurada para o texto padrão.', 'Mensagem');
  }

  /** Insere o marcador na posição do cursor (ou no fim, se o campo não estiver focado). */
  inserirToken(token: string): void {
    const el = this.editorMensagem?.nativeElement;
    if (!el) {
      this.mensagem = `${this.mensagem}${token}`;
      return;
    }
    const inicio = el.selectionStart ?? this.mensagem.length;
    const fim = el.selectionEnd ?? inicio;
    this.mensagem = this.mensagem.slice(0, inicio) + token + this.mensagem.slice(fim);
    setTimeout(() => {
      el.focus();
      const pos = inicio + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  /** Pré-visualização com o primeiro matriculado da lista. */
  get previaMensagem(): string {
    const exemplo = this.dados?.matriculados?.[0];
    if (!exemplo) {
      return renderizarMensagemFaltas(this.mensagem, {
        nome: 'Maria Aparecida da Silva',
        sexo: 'F',
        turma: this.dados?.turmaNome ?? 'Turma',
        faltas: 2,
        ultimaData: '05/08/2026',
      });
    }
    return renderizarMensagemFaltas(this.mensagem, this.contexto(exemplo));
  }

  get nomeExemploPrevia(): string {
    return this.dados?.matriculados?.[0]?.pessoaNome ?? 'exemplo';
  }

  private contexto(m: FaltaMatriculadoConsolidado): ContextoMensagemFaltas {
    const dt = this.parseIsoData(m.dataUltimoRegistro);
    return {
      nome: m.pessoaNome,
      sexo: m.pessoaId ? this.sexoPorPessoa.get(m.pessoaId) ?? null : null,
      turma: this.dados?.turmaNome ?? '',
      faltas: m.totalFaltas,
      ultimaData: dt ? dt.toLocaleDateString('pt-BR') : '',
    };
  }

  irFrequenciaDia(): void {
    void this.router.navigate(['/frequencia', this.turmaId]);
  }

  /** yyyy-MM-dd → Date local (meia-noite) para o pipe `date`. */
  parseIsoData(iso: string | undefined): Date | null {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  get totalMatriculados(): number {
    return this.dados?.matriculados?.length ?? 0;
  }

  get comFaltaRegistrada(): number {
    return this.dados?.matriculados?.filter((m) => m.totalFaltas > 0).length ?? 0;
  }

  get somaFaltas(): number {
    return this.dados?.matriculados?.reduce((acc, m) => acc + (m.totalFaltas ?? 0), 0) ?? 0;
  }

  /** Só dígitos; assumed BR se vier 10–11 dígitos sem DDI 55. */
  /**
   * `https://wa.me/{telefone}?text=...` ou `null` se não houver celular válido.
   */
  linkWhatsappMatriculado(m: FaltaMatriculadoConsolidado): string | null {
    return linkWhatsapp(m.pessoaTelefone, renderizarMensagemFaltas(this.mensagem, this.contexto(m)));
  }
}
